import { URLS, createJurisdictionHeaders, queryClient } from '@/data';
import { useWalletStore } from '@/hooks/use-wallet';
import { ErrorResponse, isErrorResponse } from '@/utils/errors/response-handler';
import { logger } from '@/utils/logging';
import { useMutation } from '@tanstack/react-query';

import { infiniteTxnActivityKeys } from './use-infinite-transaction-activity';
import { transactionKeys } from './use-transaction-details';

const LOG_TAG = '[Cancel Withdrawal]';

/**
 * Error codes returned by `POST /auth/wallet/cancel-withdrawal`.
 * `withdrawal_no_longer_pending` (409).
 * Lock/Redis failures surface as a generic 500 with no code.
 */
export const CANCEL_WITHDRAWAL_ERROR_CODES = {
    NO_LONGER_PENDING: 'withdrawal_no_longer_pending',
} as const;

export type CancelWithdrawalErrorCode =
    (typeof CANCEL_WITHDRAWAL_ERROR_CODES)[keyof typeof CANCEL_WITHDRAWAL_ERROR_CODES];

export type CancelWithdrawalErrorResponse = ErrorResponse<CancelWithdrawalErrorCode>;

export type CancelWithdrawalResponse = {
    data: {
        user_wallet: {
            user_wallet_id: string;
            action: string;
            status: string;
            amount: number;
        };
        new_transaction: {
            user_wallet_id: string;
            original_user_wallet_id: string;
        };
    };
};

export const getCancelWithdrawalErrorCode = (error: unknown): string | undefined => {
    if (!isErrorResponse<CancelWithdrawalErrorResponse>(error)) {
        return undefined;
    }
    const code = error.error_codes?.[0];
    return typeof code === 'string' && code.length > 0 ? code : undefined;
};

/**
 * Cancel a pending withdrawal/redemption that has not yet been approved in the backoffice.
 * POST /auth/wallet/cancel-withdrawal
 * @param user_wallet_id - The `user_wallet_id` of the pending WITHDRAWAL_ATTEMPT row
 */
const cancelWithdrawal = async (user_wallet_id: string): Promise<CancelWithdrawalResponse> => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/wallet/cancel-withdrawal`, {
        method: 'POST',
        headers: {
            ...createJurisdictionHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_wallet_id }),
    });

    const responseData = await response.json();

    if (!response.ok) {
        logger.warn(LOG_TAG, 'Error cancelling withdrawal', { user_wallet_id, ...responseData });
        throw responseData;
    }

    return responseData;
};

export const useCancelWithdrawal = () => {
    return useMutation<CancelWithdrawalResponse, CancelWithdrawalErrorResponse, string>({
        mutationKey: ['cancel-withdrawal'],
        mutationFn: cancelWithdrawal,
        onSettled: (_data, _error, userWalletId) => {
            // Refetch regardless of outcome: on success the row is now WITHDRAWAL_CANCELLED; on a
            // `withdrawal_no_longer_pending` race with the backoffice the details now carry
            // `reviewed: SUCCESS` (or a resolved status), so the screen swaps the button for the
            // matching explanation.
            //
            // Returned so react-query keeps the mutation pending until the refetch lands —
            // otherwise the button flashes back enabled before the screen has the new state.
            return Promise.all([
                queryClient.invalidateQueries({ queryKey: transactionKeys.details(userWalletId) }),
                queryClient.invalidateQueries({ queryKey: infiniteTxnActivityKeys.all }),
            ]);
        },
        onSuccess: () => {
            useWalletStore.getState().actions.loadUserWallet();
        },
    });
};
